import styled from "styled-components"

import { colors } from "@minuthq/meatball-ui-react"
import { roundToNearestMinutes } from "date-fns"

import { PlanDataTable } from "src/components/Account/BillingPortal/PlanDataTable"
import { useUserExpiringHomeTokens } from "src/data/homes/hooks/useExpiringHomeTokens"
import { useFetchUserHomeTokens } from "src/data/homes/queries/homeTokenQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useHomeTokens } from "src/hooks/useHomeTokens"
import { useTranslate } from "src/i18n/useTranslate"
import InfoFilled from "src/ui/icons/important-filled.svg"
import { MInfo } from "src/ui/Info/MInfo"
import { ImportantBanner } from "src/ui/MBanner/ImportantBanner"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { MText } from "src/ui/MText"
import { spacing } from "src/ui/spacing"
import { formatDate } from "src/utils/l10n"

export function CreditBalanceSection({ credits }: { credits: string }) {
  const { t, langKeys } = useTranslate()
  const { hasHomeTokenRole } = useHomeTokens()
  const user = useGetUser()

  const fetchUserHomeTokens = useFetchUserHomeTokens({
    userId: user.user_id,
    filter: {
      activated: false,
      // Round to the nearest 10 minutes
      // By rounding we make sure that the cache key is the same during re-renders within the same 10 minute window
      // If we don't round the cache key would change on every re-render and we would fetch new data every time
      expires_after: roundToNearestMinutes(new Date(), {
        nearestTo: 10,
        roundingMethod: "ceil",
      }).toISOString(),
    },
    options: { enabled: hasHomeTokenRole },
  })

  const {
    hasExpiringHomeTokens,
    expiringHomeTokensCount,
    earliestExpiringHomeToken,
  } = useUserExpiringHomeTokens()

  const homeTokensCount = fetchUserHomeTokens.data?.paging.total_count || 0

  return (
    <Box>
      <MText variant="heading3">
        {t(langKeys.subscription_account_balance_title)}
      </MText>
      {hasExpiringHomeTokens && (
        <ExpirationWrapper>
          <ImportantBanner type="warning" fullWidth>
            {t(langKeys.home_token_expiration_banner_body, {
              count: expiringHomeTokensCount,
              date: formatDate({
                date: earliestExpiringHomeToken,
                clockType: user.clock_type,
                excludeTime: true,
              }),
            })}
          </ImportantBanner>
        </ExpirationWrapper>
      )}

      <PlanDataTable
        planData={[
          {
            key: "prepaid-homes",
            title: (
              <InfoTitle>
                {t(langKeys.prepaid_homes_text)}{" "}
                <MInfo
                  content={t(
                    langKeys.subscription_available_prepaid_homes_description
                  )}
                />
              </InfoTitle>
            ),
            value: (
              <AvailableHomeTokens
                quantity={homeTokensCount}
                loading={fetchUserHomeTokens.isInitialLoading}
                error={fetchUserHomeTokens.isError}
              />
            ),
            skip: !hasHomeTokenRole,
          },
          {
            key: "credit-balance",
            title: (
              <InfoTitle>
                {t(langKeys.subscription_credit_balance)}{" "}
                <MInfo
                  content={t(langKeys.subscription_credit_balance_description)}
                />
              </InfoTitle>
            ),
            value: <MText variant="body">{credits}</MText>,
          },
        ]}
      />
    </Box>
  )
}

function AvailableHomeTokens({
  quantity,
  loading,
  error,
}: {
  quantity: number
  loading: boolean
  error: boolean
}) {
  const { t, langKeys } = useTranslate()

  if (loading) {
    return (
      <ErrorBox>
        <MSkeleton
          variant="text"
          width={30}
          height={30}
          style={{ backgroundColor: colors.neutralDark }}
        />
      </ErrorBox>
    )
  }

  if (error) {
    return (
      <ErrorBox>
        <InfoFilled fill={colors.systemError} width={20} />
        <MText color="emergency">{t(langKeys.failed_to_load)}</MText>
      </ErrorBox>
    )
  }

  return <div>{quantity}</div>
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.S};
`

const ErrorBox = styled.div`
  display: flex;
  gap: ${spacing.XS};
  justify-content: flex-end;
`

const InfoTitle = styled.div`
  display: inline-flex;
  gap: ${spacing.XS};
`

const ExpirationWrapper = styled.div`
  margin-bottom: ${spacing.M};
`
